import React, { useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { useRouter } from '@shared/hooks/use-router'
import { apiJSON } from '@shared/utils/api/api.util'
import { useQuery } from 'react-query'
import { CompanyProfile, Newsletter, Roles, Scope } from '@shared/types'
import { originalIcons } from '@shared/components/icons/icons'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import { Table } from '@shared/components/table/table.component'
import Button from '@mui/material/Button'
import { useScopesQuery } from '@shared/hooks/use-scopes-query.hook'
import {
  ListItemText,
  MenuItem,
  Select,
  OutlinedInput,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Checkbox,
  CircularProgress,
} from '@mui/material'
import handleDownload from '@shared/utils/handle-download'
import ids from '@shared/onboarding/ids'
import Joyride from 'react-joyride'
import { newslettersPageSteps } from '@shared/onboarding/steps'
import { onboardingConfig } from '@shared/onboarding/config'
import {
  OnboardingType,
  useOnboardingStore,
} from '@shared/onboarding/onboarding.store'
import handleOpenFile from '@shared/utils/handle-open-file'
import { ACL } from '@shared/components/acl.component'

const getNewsletterName = (
  newsletter: Newsletter,
  availableScopes?: Scope[]
) => {
  const scope = availableScopes?.find((s) => s.scopeID === newsletter.scopeId)
  const formattedScopeName = scope?.name
    ? scope.name.charAt(0).toUpperCase() + scope.name.slice(1).toLowerCase()
    : ''

  return `${newsletter.type === 'ACT_DESCRIPTION' ? `${newsletter?.title}` : `${formattedScopeName}`} `
}

const getNewsletterDate = (newsletter: Newsletter) => {
  const formattedDate = new Date(newsletter.createdAt).toLocaleDateString(
    'pl-PL',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }
  )

  return formattedDate
}

export function NewslettersPage() {
  const { isActive, setActive } = useOnboardingStore()

  const { query, navigate } = useRouter<{
    companyID: number
  }>()
  const { companyID } = query
  const [selectedScopeIds, setSelectedScopeIds] = useState<number[]>([])
  const { areScopesLoading, scopes: fetchedScopes } = useScopesQuery()

  const { isLoading: isConfigLoading, data: config } = useQuery<CompanyProfile>(
    'configScopes2',
    () => apiJSON(`CompaniesEvaluationProfiles/${companyID}`)
  )

  const scopesToRender = [
    ...(config?.assessedScopes || []),
    ...(config?.scopesInEsqula || []),
  ]

  const availableScopes = fetchedScopes
    ?.filter((scope) =>
      scopesToRender?.some((scoped) => scoped.id === scope.scopeID)
    )
    .map((scope) => ({
      ...scope,
      name:
        scope.name.charAt(0).toUpperCase() + scope.name.slice(1).toLowerCase(),
    }))

  const { data: newsletters, isLoading: isLoadingNewsletters } = useQuery<
    Newsletter[]
  >(
    ['newsletters', availableScopes, selectedScopeIds],
    () =>
      apiJSON(
        `newsletters/scopes?scopesIds=${(selectedScopeIds.length
          ? selectedScopeIds
          : availableScopes?.map((scope) => scope.scopeID)
        )?.join(',')}`
      ),
    {
      enabled: !!availableScopes?.length,
    }
  )

  const [downloadingIds, setDownloadingIds] = useState<number[]>([])

  const classes = useStyles()
  const themeClasses = useThemeStyles()
  const columns = [
    {
      field: 'dateId',
      title: 'Data',
      sorting: true,
      defaultSort: 'desc' as const,
      width: '100px',
      render: (newsletter: Newsletter) => {
        return getNewsletterDate(newsletter)
      },
    },
    {
      field: 'scopeId',
      title: 'Nazwa',
      sorting: false,
      render: (newsletter: Newsletter) => {
        return getNewsletterName(newsletter, availableScopes)
      },
    },
    {
      field: 'type',
      title: 'Typ',
      width: '300px',
      render: (newsletter: Newsletter) => {
        switch (newsletter.type) {
          case 'ACT':
            return 'Aktualności prawne'
          case 'ACT_DESCRIPTION':
            return 'Opis aktu prawnego'
        }
        return newsletter.type
      },
    },
    {
      field: 'id',
      title: '',
      width: '10px',
      sorting: false,
      render: (newsletter: Newsletter) => {
        const isDownloading = downloadingIds.includes(newsletter.id)
        return (
          <Button
            onClick={async () => {
              try {
                setDownloadingIds((prevState) => [...prevState, newsletter.id])
                await handleDownload(
                  `newsletters/${newsletter.id}/download`,
                  `${getNewsletterName(newsletter, availableScopes)}.pdf`
                )
              } catch (error) {
                console.error('Error downloading newsletter', error)
              } finally {
                setDownloadingIds((prevState) =>
                  prevState.filter((id) => id !== newsletter.id)
                )
              }
            }}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <CircularProgress size={24} />
            ) : (
              <originalIcons.Download />
            )}
          </Button>
        )
      },
    },
    {
      field: 'id',
      title: '',
      width: '10px',
      sorting: false,
      render: (newsletter: Newsletter) => {
        return (
          <Button
            sx={{ marginLeft: '-40px' }}
            onClick={() => handleOpenFile(`newsletters/${newsletter.id}`)}
          >
            <originalIcons.Visibility />
          </Button>
        )
      },
    },
  ]
  const isLoading = isConfigLoading || isLoadingNewsletters || areScopesLoading

  const enrichedNewsletters = newsletters?.map((newsletter) => ({
    ...newsletter,
    dateId: new Date(newsletter.createdAt).toISOString(),
  }))

  return (
    <div>
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.ClientBasic,
        ]}
      >
        <Joyride
          {...onboardingConfig}
          steps={newslettersPageSteps}
          continuous
          run={isActive(OnboardingType.PREVIEW_PAGE)}
          callback={(data) => {
            if (data.status === 'finished' || data.status === 'skipped') {
              setActive(OnboardingType.PREVIEW_PAGE, false)
            }
          }}
        />
      </ACL>
      <div className={classes.buttonContainer}>
        <Button
          variant="outlined"
          onClick={
            () => navigate(-1)
            // navigate(`/audit/${companyID}/${auditID}`, {
            //   replace: true,
            // })
          }
          startIcon={<originalIcons.PreviousPage />}
        >
          Powrót
        </Button>
        {availableScopes?.length ? (
          <FormControl sx={{ width: 300 }} id={ids.CHOOSE_SCOPE}>
            <InputLabel id="scopes-multiple-checkbox-label">
              Wybierz zakresy
            </InputLabel>
            <Select
              labelId="scopes-multiple-checkbox-label"
              id="scopes-multiple-checkbox"
              multiple
              value={selectedScopeIds}
              onChange={(event: SelectChangeEvent<number[]>) => {
                const value = event.target.value
                setSelectedScopeIds(
                  typeof value === 'string' ? [] : (value as number[])
                )
              }}
              input={<OutlinedInput label="Wybierz zakresy" />}
              renderValue={(selected) =>
                availableScopes
                  ?.filter((scope) => selected.includes(scope.scopeID))
                  .map((scope) => scope.name)
                  .join(', ')
              }
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 250,
                  },
                },
              }}
            >
              {availableScopes?.map((scope) => (
                <MenuItem key={scope.scopeID} value={scope.scopeID}>
                  <Checkbox
                    checked={selectedScopeIds.includes(scope.scopeID)}
                  />
                  <ListItemText primary={scope.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </div>
      <div className={themeClasses.tableContainer}>
        <Table<Newsletter>
          title="Nowości prawne"
          isLoading={isLoading}
          data={enrichedNewsletters ?? []}
          columns={columns}
        />
      </div>
    </div>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    tableContainer: {},
    groupRow: {
      display: 'inline-flex',
      justifyContent: 'space-between',
      width: 'calc(100% - 50px)',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      fontSize: 14,
      fontWeight: 500,
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
  }
})
